import React from 'react';
import { Button } from '../components/UI';
import { generateAssessmentPDF } from '../helpers/pdfGenerator';
import { kuesionerData } from '../helpers/kuesioner';
import RecommendationCard from '../components/RecommendationCard';

const KPSPStandard = ({ ageInMonths }) => {
    // Find the closest age group for KPSP questions
    const availableAges = Object.keys(kuesionerData.kpsp).map(Number);
    const closestAge = availableAges.reduce((prev, curr) => 
        (Math.abs(curr - ageInMonths) < Math.abs(prev - ageInMonths) ? curr : prev)
    );

    const questions = kuesionerData.kpsp[closestAge];

    return (
        <div className="mt-4 pt-4 border-t border-gray-200">
            <h6 className="font-bold text-xs uppercase tracking-widest text-gray-500 mb-3">
                Standar KPSP Usia {closestAge} Bulan:
            </h6>
            <ul className="space-y-2">
                {questions.map((q, idx) => (
                    <li key={q.id} className="flex items-start space-x-3">
                        <span className="flex-shrink-0 w-5 h-5 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center text-[10px] font-bold mt-0.5">
                            {idx + 1}
                        </span>
                        <span className="text-sm text-gray-600 leading-relaxed">{q.q}</span>
                    </li>
                ))}
            </ul>
        </div>
    );
};

// Fallback ResultCard for old data structures without recommendation logic
const FallbackResultCard = ({ title, data, children }) => {
    if (!data) return null;
    
    let statusColor = 'text-amber-700 bg-amber-50 border-amber-200';
    if (data.status === 'Sesuai' || data.status === 'Risiko Rendah' || data.status === 'Normal') {
        statusColor = 'text-emerald-700 bg-emerald-50 border-emerald-200';
    }
    if (data.status.includes('Penyimpangan') || data.status.includes('Risiko') || data.status.includes('Kemungkinan')) {
        statusColor = 'text-rose-700 bg-rose-50 border-rose-200';
    }

    return (
        <div className="mb-5 bg-white/70 backdrop-blur-xl border border-white/80 rounded-[1.5rem] p-5 shadow-[0_8px_30px_rgba(0,0,0,0.04)]">
            <div className="flex justify-between items-start mb-4">
                <h5 className="font-extrabold text-lg text-gray-800">{title}</h5>
            </div>
            <div className={`p-4 rounded-xl border ${statusColor}`}>
                <div className="font-extrabold">{data.status}</div>
                {data.detail && <div className="text-sm mt-1 opacity-90">{data.detail}</div>}
                <p className="text-sm mt-2 opacity-90 font-medium leading-relaxed">{data.interpretasi}</p>
            </div>
            {children}
        </div>
    );
};

export default function ResultScreen({ child, assessment, navigate }) {
    
    // Helper to determine if we should use RecommendationCard or Fallback
    const renderCard = (title, data, extraChildren = null) => {
        if (!data) return null;
        if (data.rekomendasi || data.tindakLanjut || data.followUp || data.urgency) {
            return (
                <div key={title}>
                    <RecommendationCard title={title} data={data} />
                    {extraChildren && (
                        <div className="bg-white/50 backdrop-blur-md rounded-[1.5rem] p-5 mt--2 mb-5 border border-white shadow-sm">
                            {extraChildren}
                        </div>
                    )}
                </div>
            );
        }
        return <FallbackResultCard key={title} title={title} data={data}>{extraChildren}</FallbackResultCard>;
    };

    // Filter out critical results to show them at the top
    const allResults = [
        { title: "Pertumbuhan Fisik", data: assessment.pertumbuhan },
        { title: "Perkembangan (KPSP)", data: assessment.perkembangan, extra: assessment.perkembangan ? <KPSPStandard ageInMonths={assessment.ageInMonths} /> : null },
        { title: "Mental Emosional (KMME)", data: assessment.mental },
        { title: "Skrining GPPH", data: assessment.gpph },
        { title: "Tes Daya Dengar (TDD)", data: assessment.dayaDengar },
        { title: "Pemeriksaan Mata (Pupil Putih)", data: assessment.pemeriksaanMata?.pupilPutih },
        { title: "Pemeriksaan Mata (Daya Lihat)", data: assessment.pemeriksaanMata?.dayaLihat },
        { title: "Skrining Autisme (CHAT)", data: assessment.chat },
    ].filter(item => item.data);

    const criticalResults = allResults.filter(item => (item.data.urgency === 'high' || item.data.tingkatUrgency === 'high'));
    const normalResults = allResults.filter(item => !(item.data.urgency === 'high' || item.data.tingkatUrgency === 'high'));

    return (
        <div className="min-h-screen bg-[#F8FAFC] font-sans selection:bg-indigo-200 pb-28 pt-8 px-5 relative">
            {/* Animated Background Mesh */}
            <div className="fixed inset-0 z-0 overflow-hidden pointer-events-none">
                <div className="absolute top-0 right-[-10%] w-[50vw] h-[50vw] bg-indigo-200 rounded-full mix-blend-multiply filter blur-[100px] opacity-40 animate-blob"></div>
                <div className="absolute bottom-[20%] left-[-20%] w-[60vw] h-[60vw] bg-sky-200 rounded-full mix-blend-multiply filter blur-[100px] opacity-30 animate-blob animation-delay-2000"></div>
                <div className="absolute inset-0 bg-white/40 backdrop-blur-[50px]"></div>
            </div>

            <div className="relative z-10 max-w-lg mx-auto">
                {/* Header Card */}
                <div className="bg-white/60 backdrop-blur-2xl rounded-[2rem] p-6 border border-white shadow-[0_8px_30px_rgba(0,0,0,0.04)] mb-8 text-center">
                    <div className="w-16 h-16 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-[1.25rem] flex items-center justify-center text-3xl mx-auto mb-4 shadow-sm border border-white">
                        📋
                    </div>
                    <h2 className="text-2xl font-extrabold text-gray-800 tracking-tight">{child.name}</h2>
                    <p className="text-sm text-gray-500 mt-2 font-medium">
                        Hasil Pemeriksaan pada <br/>
                        <span className="text-indigo-600 font-bold">
                            {assessment.createdAt.toDate().toLocaleString('id-ID', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        </span>
                    </p>
                    
                    <button 
                        onClick={() => generateAssessmentPDF(child, assessment)}
                        className="mt-6 w-full bg-white border-2 border-gray-100 text-gray-700 font-bold py-3.5 rounded-xl hover:bg-gray-50 transition-all flex items-center justify-center space-x-2"
                    >
                        <span>📥</span>
                        <span>Unduh Laporan (PDF)</span>
                    </button>
                </div>

                {/* Critical Results Section */}
                {criticalResults.length > 0 && (
                    <div className="mb-8">
                        <div className="flex items-center space-x-2 mb-4 px-2">
                            <span className="text-xl">⚠️</span>
                            <h3 className="text-xl font-extrabold text-rose-600 tracking-tight">Peringatan Kritis</h3>
                        </div>
                        {criticalResults.map(item => renderCard(item.title, item.data, item.extra))}
                    </div>
                )}

                {/* Normal Results Section */}
                {normalResults.length > 0 && (
                    <div>
                        <div className="flex items-center space-x-2 mb-4 px-2">
                            <span className="text-xl">📝</span>
                            <h3 className="text-xl font-extrabold text-gray-800 tracking-tight">Rincian Hasil</h3>
                        </div>
                        {normalResults.map(item => renderCard(item.title, item.data, item.extra))}
                    </div>
                )}

                <Button 
                    onClick={() => navigate('viewChild', child)}
                    className="w-full bg-gray-900 text-white hover:bg-gray-800 shadow-xl py-4 rounded-xl mt-6 transition-all"
                >
                    Kembali ke Detail Anak
                </Button>
            </div>

            <style>{`
                @keyframes blob {
                    0% { transform: translate(0px, 0px) scale(1); }
                    33% { transform: translate(30px, -50px) scale(1.1); }
                    66% { transform: translate(-20px, 20px) scale(0.9); }
                    100% { transform: translate(0px, 0px) scale(1); }
                }
                .animate-blob { animation: blob 15s infinite alternate ease-in-out; }
                .animation-delay-2000 { animation-delay: 2s; }
            `}</style>
        </div>
    );
}
